import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

export type RoleListItem = {
  id: string
  slug: string
  displayName: string
  fullAccess: boolean
  isSystem: boolean
  createdBy: string | null
  permissionKeys: string[]
}

export type CreateRolePayload = {
  slug: string
  displayName: string
  fullAccess?: boolean
  permissionKeys?: string[]
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? text
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const rolesApi = {
  list: () => request<RoleListItem[]>('/roles'),

  get: (id: string) => request<RoleListItem>(`/roles/${id}`),

  create: (payload: CreateRolePayload) =>
    request<RoleListItem>('/roles', { method: 'POST', body: JSON.stringify(payload) }),

  update: (id: string, payload: { displayName?: string; slug?: string }) =>
    request<RoleListItem>(`/roles/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),

  setPermissions: (id: string, permissionKeys: string[]) =>
    request<RoleListItem>(`/roles/${id}/permissions`, {
      method: 'PATCH',
      body: JSON.stringify({ permissionKeys }),
    }),

  remove: (id: string) => request<void>(`/roles/${id}`, { method: 'DELETE' }),
}
